import axios from 'axios';

import { accountService } from '@/services/accounts';
import type { Account } from '@/services/accounts';
import type { Institution } from '@/services/institutions';
import type { PaginatedResponse } from '@/services/_base';
import { getApiEndpoint } from '@/globals/api-endpoint';
import { session } from '@/globals/session';

const ADMIN_AUTH_TOKEN_KEY = 'adminAuthToken';

type ReloadPage = () => void;

let isSwitchingSession = false;

interface ImpersonationSession {
  account: Account;
  institution: Institution;
}

async function loadSession(accessToken: string): Promise<ImpersonationSession> {
  const config = {
    baseURL: getApiEndpoint(),
    timeout: 10000,
    headers: { Authorization: `Bearer ${accessToken}` },
  };
  const [accountsResponse, institutionsResponse] = await Promise.all([
    axios.get<PaginatedResponse<Account>>('/api/v2/accounts/users/?limit=1', config),
    axios.get<PaginatedResponse<Institution>>('/api/v2/accounts/institutions/?limit=1', config),
  ]);
  const account = accountsResponse.data.results[0];
  const institution = institutionsResponse.data.results[0];
  if (!account || !institution) throw new Error('Could not initialise the target session.');
  return { account, institution };
}

async function switchSession(accessToken: string, currentAccessToken: string | null) {
  // Validate and load the target with an isolated request. Until both calls
  // succeed, the active token, session, and local offline data stay untouched.
  const targetSession = await loadSession(accessToken);
  if (localStorage.getItem('accessToken') !== currentAccessToken) {
    throw new Error('The active session changed while switching sessions.');
  }

  // Keep the destructive swap atomic: there must be no await between logout
  // and installing the complete target session.
  accountService.logout({ preserveAdminAuthToken: true });
  localStorage.setItem('accessToken', accessToken);
  session.setAccount(targetSession.account);
  session.setInstitution(targetSession.institution);
}

async function startImpersonationSession(
  accessToken: string,
  reloadPage: ReloadPage = () => window.location.reload(),
) {
  if (isSwitchingSession) return;
  const adminAuthToken = localStorage.getItem('accessToken');
  if (!adminAuthToken) return;

  isSwitchingSession = true;
  try {
    const previousAdminAuthToken = localStorage.getItem(ADMIN_AUTH_TOKEN_KEY);
    localStorage.setItem(ADMIN_AUTH_TOKEN_KEY, previousAdminAuthToken || adminAuthToken);

    try {
      await switchSession(accessToken, adminAuthToken);
    } catch (error) {
      if (previousAdminAuthToken) {
        localStorage.setItem(ADMIN_AUTH_TOKEN_KEY, previousAdminAuthToken);
      } else {
        localStorage.removeItem(ADMIN_AUTH_TOKEN_KEY);
      }
      throw error;
    }

    // The customer token cannot reload the staff detail route it came from.
    window.history.replaceState(window.history.state, '', '/');
    reloadPage();
  } finally {
    isSwitchingSession = false;
  }
}

async function endImpersonationSession(
  reloadPage: ReloadPage = () => window.location.reload(),
) {
  if (isSwitchingSession) return;
  const adminAuthToken = localStorage.getItem(ADMIN_AUTH_TOKEN_KEY);
  if (!adminAuthToken) return;
  const currentAccessToken = localStorage.getItem('accessToken');

  isSwitchingSession = true;
  try {
    await switchSession(adminAuthToken, currentAccessToken);
    localStorage.removeItem(ADMIN_AUTH_TOKEN_KEY);
    window.history.replaceState(window.history.state, '', '/');
    reloadPage();
  } finally {
    isSwitchingSession = false;
  }
}

export {
  ADMIN_AUTH_TOKEN_KEY,
  endImpersonationSession,
  startImpersonationSession,
};
